import { parseScopedSearchQuery } from './components/SearchFiltersBar';

const fmt = new Intl.NumberFormat('mt-MT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/**
 * @param {{
 *   effectiveQuery?: string;
 *   shopCategorySlug?: string;
 *   saleActive?: boolean;
 *   activeWebsiteHost?: string;
 *   productCategoryId?: string;
 *   onlyInStock?: boolean;
 *   filterPriceMin?: number | null;
 *   filterPriceMax?: number | null;
 *   priceBounds?: { min: number; max: number } | null;
 *   selectedRetailers?: Set<string> | Iterable<string>;
 * }} scope
 */
export function hasSaveableSearchScope(scope) {
  const q = (scope.effectiveQuery ?? '').trim();
  const shop = (scope.shopCategorySlug ?? '').trim();
  const sale = scope.saleActive === true;
  const website = (scope.activeWebsiteHost ?? '').trim();
  const categoryId = (scope.productCategoryId ?? '').trim();
  const onlyInStock = scope.onlyInStock === true;
  const retailers = scope.selectedRetailers
    ? [...scope.selectedRetailers]
    : [];
  const bounds = scope.priceBounds;
  const min = scope.filterPriceMin;
  const max = scope.filterPriceMax;
  const hasPriceFilter =
    bounds != null
    && min != null
    && max != null
    && (min > bounds.min || max < bounds.max);

  return (
    q !== ''
    || shop !== ''
    || sale
    || website !== ''
    || categoryId !== ''
    || onlyInStock
    || retailers.length > 0
    || hasPriceFilter
  );
}

/**
 * @param {Parameters<typeof hasSaveableSearchScope>[0]} scope
 */
export function buildSearchAlertPath(scope) {
  const params = new URLSearchParams();
  const q = (scope.effectiveQuery ?? '').trim();
  const shop = (scope.shopCategorySlug ?? '').trim();
  const website = (scope.activeWebsiteHost ?? '').trim();
  const categoryId = (scope.productCategoryId ?? '').trim();

  if (q !== '') {
    params.set('q', q);
  }
  if (shop !== '') {
    params.set('shop', shop);
  }
  if (scope.saleActive === true) {
    params.set('sale', '1');
  }
  if (website !== '') {
    params.set('website', website);
  }
  if (categoryId !== '') {
    params.set('category', categoryId);
  }
  if (scope.onlyInStock === true) {
    params.set('in_stock', '1');
  }

  const bounds = scope.priceBounds;
  const min = scope.filterPriceMin;
  const max = scope.filterPriceMax;
  if (
    bounds != null
    && min != null
    && max != null
    && (min > bounds.min || max < bounds.max)
  ) {
    params.set('price_min', String(min));
    params.set('price_max', String(max));
  }

  const retailers = scope.selectedRetailers
    ? [...scope.selectedRetailers].sort((a, b) => a.localeCompare(b))
    : [];
  for (const name of retailers) {
    params.append('retailer', name);
  }

  const qs = params.toString();
  return qs !== '' ? `/search?${qs}` : '/';
}

/**
 * @param {{
 *   effectiveQuery?: string;
 *   shopCategorySlug?: string;
 *   saleActive?: boolean;
 *   browseCategories?: Array<{ slug?: string; name?: string; subcategories?: Array<{ name?: string; search?: string }> }>;
 *   onlyInStock?: boolean;
 *   filterPriceMin?: number | null;
 *   filterPriceMax?: number | null;
 *   priceBounds?: { min: number; max: number } | null;
 *   selectedRetailers?: Set<string> | Iterable<string>;
 *   activeWebsiteHost?: string;
 * }} input
 */
export function buildSearchAlertPresentation(input) {
  const browseCategories = Array.isArray(input.browseCategories)
    ? input.browseCategories
    : [];
  const shopSlug = (input.shopCategorySlug ?? '').toLowerCase();
  const scoped = parseScopedSearchQuery(
    input.effectiveQuery ?? '',
    browseCategories,
    shopSlug,
  );
  const category = browseCategories.find((c) => c.slug === shopSlug);
  const categoryName =
    typeof category?.name === 'string' ? category.name.trim() : '';

  let scopeLabel = 'this search';
  if (input.saleActive === true && scoped.userQuery.trim() === '' && !scoped.subcategory) {
    scopeLabel = shopSlug && categoryName
      ? `sale items in ${categoryName}`
      : 'sale items';
  } else if (scoped.subcategory) {
    const subName =
      typeof scoped.subcategory.name === 'string'
        ? scoped.subcategory.name.trim()
        : '';
    if (categoryName && subName) {
      scopeLabel = `${categoryName} › ${subName}`;
    } else if (subName) {
      scopeLabel = subName;
    }
  } else if (shopSlug && categoryName && scoped.userQuery.trim() === '') {
    scopeLabel = categoryName;
  } else if (scoped.userQuery.trim() !== '') {
    scopeLabel = `“${scoped.userQuery.trim()}”`;
    if (categoryName) {
      scopeLabel = `${scopeLabel} in ${categoryName}`;
    }
  } else if (categoryName) {
    scopeLabel = categoryName;
  } else if (input.saleActive === true) {
    scopeLabel = 'sale items';
  }

  const chips = [];
  if (categoryName && !scoped.subcategory && scoped.userQuery.trim() !== '') {
    chips.push(categoryName);
  }
  if (input.saleActive === true) {
    chips.push('On sale');
  }
  if (input.onlyInStock === true) {
    chips.push('In stock only');
  }

  const bounds = input.priceBounds;
  const min = input.filterPriceMin;
  const max = input.filterPriceMax;
  if (
    bounds != null
    && min != null
    && max != null
    && (min > bounds.min || max < bounds.max)
  ) {
    chips.push(`${fmt.format(min)} – ${fmt.format(max)}`);
  }

  const retailers = input.selectedRetailers
    ? [...input.selectedRetailers].sort((a, b) => a.localeCompare(b))
    : [];
  if (retailers.length === 1) {
    chips.push(retailers[0]);
  } else if (retailers.length > 1) {
    chips.push(`${retailers.length} stores`);
  }

  const website = (input.activeWebsiteHost ?? '').trim();
  if (website !== '') {
    chips.push(website);
  }

  return { scopeLabel, chips };
}

/**
 * Build scope for a shop category hub (no query).
 *
 * @param {string} shopSlug
 * @param {string} categoryName
 */
export function buildCategoryHubAlertScope(shopSlug, categoryName) {
  const slug = shopSlug.trim().toLowerCase();
  const name = categoryName.trim() || slug;
  return {
    searchPath: slug ? `/search?shop=${encodeURIComponent(slug)}` : '/',
    scopeLabel: name,
    chips: [],
  };
}

/**
 * @param {string} shopSlug
 * @param {string} categoryName
 * @param {{ name?: string; search?: string }} subcategory
 */
export function buildSubcategoryAlertScope(shopSlug, categoryName, subcategory) {
  const slug = shopSlug.trim().toLowerCase();
  const catName = categoryName.trim();
  const subName =
    typeof subcategory.name === 'string' ? subcategory.name.trim() : '';
  const subSearch =
    typeof subcategory.search === 'string' ? subcategory.search.trim() : '';
  const params = new URLSearchParams();
  if (slug) {
    params.set('shop', slug);
  }
  if (subSearch !== '') {
    params.set('q', subSearch);
  }
  const qs = params.toString();
  const scopeLabel =
    catName && subName ? `${catName} › ${subName}` : subName || catName || 'this category';
  return {
    searchPath: qs !== '' ? `/search?${qs}` : '/',
    scopeLabel,
    chips: catName && subName ? [catName] : [],
  };
}
